import { Box, Button, Icon, Text } from "@salestrak/ui";
import {
  HeaderLabel,
  IconTile,
  PanelCard,
  ScreenHeader,
  StatCard,
  StatusDot,
  TableHeaderRow,
  TableRow,
} from "./admin-ui";
import type { HealthStatus, ServiceHealthCheck } from "./use-admin-health";

const STATUS_COLOR = {
  up: "success",
  degraded: "warning",
  down: "destructive",
} as const;

const STATUS_LABEL: Record<HealthStatus, string> = {
  up: "Operational",
  degraded: "Degraded",
  down: "Down",
};

export function SystemHealthScreen({
  services,
  checking,
  lastChecked,
  recheck,
  total,
  upCount,
  degradedCount,
  downCount,
  avgMs,
}: {
  services: ServiceHealthCheck[];
  checking: boolean;
  lastChecked: string | null;
  recheck: () => void;
  total: number;
  upCount: number;
  degradedCount: number;
  downCount: number;
  avgMs: number | null;
}) {
  return (
    <Box fill="flex" overflow="auto">
      <ScreenHeader
        title="System health"
        meta={lastChecked ? `Checked ${lastChecked}` : undefined}
        actions={
          <Button variant="outline" size="sm" disabled={checking} onClick={recheck}>
            <Icon name="refresh-cw" size="sm" color="muted" />
            <Text>{checking ? "Checking…" : "Re-check"}</Text>
          </Button>
        }
      />

      <Box direction="row" gap="sm" paddingX="lg" paddingTop="sm">
        <StatCard label="Services up" value={`${upCount}`} suffix={`/ ${total}`} color="success" />
        <StatCard label="Degraded" value={`${degradedCount}`} color={degradedCount ? "warning" : undefined} />
        <StatCard label="Down" value={`${downCount}`} color={downCount ? "destructive" : undefined} />
        <StatCard label="Avg response" value={avgMs == null ? "—" : `${avgMs}`} suffix="ms" />
      </Box>

      <Box paddingX="lg" paddingBottom="xl" paddingTop="md">
        <PanelCard>
          <TableHeaderRow>
            <HeaderLabel fill>Service</HeaderLabel>
            <HeaderLabel fill>Health endpoint</HeaderLabel>
            <HeaderLabel width={130}>Status</HeaderLabel>
            <HeaderLabel width={90} align="right">
              Latency
            </HeaderLabel>
            <HeaderLabel width={100}>Last check</HeaderLabel>
          </TableHeaderRow>

          {services.map((s, index) => (
            <TableRow key={`${s.name}-${index}`} last={index === services.length - 1}>
              <Box fill="flex" direction="row" align="center" gap="sm">
                <IconTile icon="box" />
                <Box direction="column">
                  <Text size="sm" weight="medium">
                    {s.name}
                  </Text>
                  <Text size="xs" variant="muted">
                    {s.kind}
                  </Text>
                </Box>
              </Box>
              <Box fill="flex" direction="row" align="center" gap="xs">
                <Text size="2xs" weight="medium" variant="muted" mono>
                  GET
                </Text>
                <Text size="sm" mono variant="info" truncate={1}>
                  {s.url}
                </Text>
              </Box>
              <Box width={130} direction="row" align="center" gap="xs">
                <StatusDot background={STATUS_COLOR[s.status]} />
                <Text size="sm" weight="medium" variant={STATUS_COLOR[s.status]}>
                  {STATUS_LABEL[s.status]}
                </Text>
              </Box>
              <Box width={90} align="end">
                <Text size="sm" mono variant={s.status === "down" ? "destructive" : undefined}>
                  {s.latencyMs == null ? "—" : `${s.latencyMs}ms`}
                </Text>
              </Box>
              <Box width={100}>
                <Text size="sm" variant="muted" mono>
                  {s.lastCheck}
                </Text>
              </Box>
            </TableRow>
          ))}
        </PanelCard>

        <Box direction="row" align="center" gap="xs" paddingTop="sm">
          <Icon name="info" size="xs" color="muted" />
          <Text size="xs" variant="muted">
            Each service exposes a /health endpoint, pinged live from your
            browser. Latency is round-trip from this device.
          </Text>
        </Box>
      </Box>
    </Box>
  );
}
